import { FormikProps } from 'formik'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { dateBackendDateTimeToUIUtcTime } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CustomTimeRange } from 'modules/attendance/hooks/useFetchCustomTimeRanges'
import { useUpdateCustomTimeRange } from 'modules/attendance/hooks/useUpdateCustomTimeRange'

import { CustomTimeRangesFormData } from './utils/CustomTimeRangesFormData'
import useHandleReumbursableMappings from './utils/useHandleReimbursableMappings'

interface Props {
  settingId: string
  customTimeRange: CustomTimeRange
}

const createCustomTimeRanges = (data: CustomTimeRangesFormData) => {
  const defaultDate = '2000-01-01'
  const start_at = moment.utc(`${defaultDate} ${data.start_time}`).toISOString()
  const end_at = moment.utc(`${defaultDate} ${data.end_time}`).toISOString()

  return data.day.map((selectedDay) => ({
    id: data.id!,
    attendanceCustomTimeRangeCategoryId: data.attendance_custom_time_range_category_id,
    poolType: data.pool_type,
    day: selectedDay,
    startTime: start_at,
    endTime: end_at,
    timeSettingsCustomTimeRangeCategoryId: data.time_settings_custom_time_range_category_id,
    attendanceEmployeesSettingId: data.attendance_employees_setting_id,
  }))
}

const EditCustomTimeRangesForm = ({ settingId, customTimeRange }: Props) => {
  const { update } = useUpdateCustomTimeRange()
  const { manageMappings } = useHandleReumbursableMappings()

  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const defaultValues: CustomTimeRangesFormData = {
    id: customTimeRange.id.toString(),
    day: customTimeRange.day ? [customTimeRange.day] : [],
    start_time: customTimeRange.startTime
      ? dateBackendDateTimeToUIUtcTime(customTimeRange.startTime)
      : undefined,
    end_time: customTimeRange.endTime
      ? dateBackendDateTimeToUIUtcTime(customTimeRange.endTime)
      : undefined,
    attendance_custom_time_range_category_id:
      customTimeRange.attendanceCustomTimeRangeCategoryId?.toString() ?? '',
    pool_type: customTimeRange.poolType,
    attendance_employees_setting_id: settingId.toString(),
    reimbursable_taxonomies_mappings: [],
    time_settings_custom_time_range_category_id: '',
  }

  const form: FormikProps<CustomTimeRangesFormData> = useForm({
    defaultValues: defaultValues,
    onSubmit: async (data, { setErrors }) => {
      const customTimeRanges = createCustomTimeRanges(data)
      const payload = {
        companyId: currentCompanyId,
        attendanceEmployeesSettingId: data.attendance_employees_setting_id,
        customTimeRanges,
      }

      const result = await update(payload)

      const handled = handleResult(result, ({ day }) => {
        setErrors({ day })
      })

      if (handled.success) {
        const mappings = data.reimbursable_taxonomies_mappings || []

        manageMappings(setErrors, mappings)

        if (payload.attendanceEmployeesSettingId === settingId) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
        }
      }

      form.resetForm()

      return handled
    },
  })

  return form
}

export default EditCustomTimeRangesForm
